import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Star, Award, Heart, Gem, IndianRupee, ShoppingCart, Truck, Gift,
  PawPrint, Tag, Check, ChevronRight, ChevronLeft, Plus, Zap,
} from 'lucide-react';
import { getProductById, getRelated } from '../services/api';
import ProductCard from '../components/ProductCard';
import ProductReviews from '../components/ProductReviews';
import { useWishlist } from '../context/WishlistContext';
import useSeo from '../hooks/useSeo';

const tabs = ['PRODUCT DETAILS', 'WHAT TO EXPECT', 'RETURN & POLICY'];

export default function Product({ onAddToCart, cart }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const { has, toggle } = useWishlist();

  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [tab, setTab] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [time, setTime] = useState({ h: 3, m: 11, s: 28 });
  const [thumbStart, setThumbStart] = useState(0);

  useEffect(() => {
    setProduct(null);
   getProductById(id).then(p => {
  const media = [
    ...(p?.gallery?.length ? p.gallery : (p?.image ? [p.image] : [])),
    ...(p?.videos || []),
  ];
  setProduct(p ? { ...p, gallery: media } : p);
  setActiveImg(0);
  setSize(p?.sizes?.[0] || null);
  window.scrollTo({ top: 0, behavior: 'smooth' });
});
  }, [id]);

  useEffect(() => {
    if (!product) return;
    getRelated(product, 6).then(setSuggestions);
  }, [product]);

  useEffect(() => {
    const t = setInterval(() => {
      setTime(prev => {
        let { h, m, s } = prev;
        if (s > 0) s--; else { s = 59; if (m > 0) m--; else { m = 59; if (h > 0) h--; } }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useSeo({
    title:       product?.meta_title       || product?.title || '',
    description: product?.meta_description || product?.short_description || (product?.description || '').slice(0, 160),
    image:       product?.image,
    type:        'product',
    canonical:   product ? `/product/${product.slug}` : '',
    jsonLd: product ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': product.title.split('|')[0].trim(),
      'image': [product.image, ...(product.gallery || [])].filter(Boolean).slice(0, 6),
      'description': product.short_description || (product.description || '').replace(/<[^>]+>/g, ' ').slice(0, 500),
      'sku': product.id,
      'brand': { '@type': 'Brand', 'name': 'Maa Florist Mumbai' },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'INR',
        'price': product.price,
        'availability': product.stock_status === 'out_of_stock'
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock',
        'url': typeof window !== 'undefined' ? window.location.href : '',
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': product.rating || 5,
        'reviewCount': product.reviews || 100,
      },
    } : null,
  });

  if (!product) {
    return <main className="min-h-screen grid place-items-center text-neutral-500">Loading...</main>;
  }

  const liked = has(product.id);

  const pad = n => String(n).padStart(2, '0');

  return (
    <main className="bg-white pb-28 lg:pb-10">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-2">
        <nav className="flex items-center gap-1.5 text-xs text-neutral-500 flex-wrap">
          <Link to="/" className="hover:text-[#1e3a2b]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to={`/collection/${product.category}`} className="hover:text-[#1e3a2b] capitalize">{product.category.replace('-', ' ')}</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#1e3a2b] font-medium line-clamp-1">{product.title.split('|')[0]}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 lg:gap-12 pt-4">
        {/* Gallery */}
        <div>
          <div className="rounded-xl overflow-hidden bg-neutral-100 aspect-square">
            {(product.gallery[activeImg] || '').match(/\.(mp4|webm|mov)(\?|$)/i) ? (
              <video src={product.gallery[activeImg]} controls autoPlay muted loop playsInline className="w-full h-full object-cover" />
            ) : (
              <img src={product.gallery[activeImg]} alt={product.title} className="w-full h-full object-cover" />
            )}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              className="p-2 rounded-full hover:bg-neutral-100 disabled:opacity-30 transition-colors"
              disabled={thumbStart === 0}
              onClick={() => setThumbStart(Math.max(0, thumbStart - 1))}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex gap-2 overflow-hidden">
              {product.gallery.slice(thumbStart, thumbStart + 5).map((g, i) => {
                const idx = i + thumbStart;
                const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(g);
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all relative ${activeImg === idx ? 'border-[#b8935a]' : 'border-neutral-200 hover:border-neutral-400'}`}
                  >
                    {isVideo ? (
                      <>
                        <video src={g} muted playsInline className="w-full h-full object-cover" />
                        <span className="absolute inset-0 grid place-items-center bg-black/40 text-white text-lg font-bold">&#9654;</span>
                      </>
                    ) : (
                      <img src={g} alt="" className="w-full h-full object-cover" />
                    )}
                  </button>
                );
              })}
            </div>
            <button
              className="p-2 rounded-full hover:bg-neutral-100 disabled:opacity-30 transition-colors"
              disabled={thumbStart + 5 >= product.gallery.length}
              onClick={() => setThumbStart(Math.min(product.gallery.length - 5, thumbStart + 1))}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
            <span className="text-sm text-neutral-700">({product.reviews}+ Reviews)</span>
          </div>

          <div className="mt-3 flex items-center gap-2 text-sm text-neutral-700">
            <Award className="w-4 h-4 text-[#b8935a]" />
            <span>RANKED <b className="text-neutral-900">NO.1 Flower BY GOOGLE</b> IN INDIA</span>
          </div>

          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-neutral-900 leading-[1.1]">
            {product.title.split('|')[0]}
          </h1>

          <button
            onClick={() => toggle(product)}
            className={`mt-3 inline-flex items-center gap-2 text-sm font-medium transition-colors ${liked ? 'text-red-600' : 'text-neutral-600 hover:text-red-600'}`}
          >
            <Heart className={`w-5 h-5 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
            {liked ? 'Saved to wishlist' : 'Add to wishlist'}
          </button>

          <div className="mt-5 flex items-end gap-3 flex-wrap">
            <span className="text-2xl sm:text-3xl font-bold text-neutral-900">Rs. {product.price.toLocaleString('en-IN')}.00</span>
            <span className="text-neutral-500 line-through text-lg">Rs. {product.originalPrice.toLocaleString('en-IN')}.00</span>
            <span className="inline-flex items-center gap-1.5 bg-[#b8935a]/20 border border-[#b8935a]/40 rounded-md px-3 py-1">
              <Tag className="w-3.5 h-3.5 text-[#8b6c3d]" />
              <span className="text-sm font-semibold text-[#8b6c3d]">SAVE Rs. {product.save.toLocaleString('en-IN')}</span>
            </span>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex -space-x-2">
              {product.gallery.slice(0, 4).map((g, i) => (
                <img key={i} src={g} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover shadow" />
              ))}
            </div>
            <div className="bg-[#1e3a2b] text-white px-4 py-2 rounded-full text-xs sm:text-sm flex items-center gap-2">
              <span className="bg-yellow-400 text-[#1e3a2b] rounded-full w-5 h-5 grid place-items-center">
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
              <span><b>Shipped 100+ orders</b> in the last 24h</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 text-center">
            <div className="flex flex-col items-center gap-1">
              <Heart className="w-8 h-8 text-[#1e3a2b]" />
              <span className="text-xs sm:text-sm text-neutral-800">100% Handmade</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Gem className="w-8 h-8 text-[#1e3a2b]" />
              <span className="text-xs sm:text-sm text-neutral-800">Premium Quality</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <IndianRupee className="w-8 h-8 text-[#1e3a2b]" />
              <span className="text-xs sm:text-sm text-neutral-800">Manufacturing prices</span>
            </div>
          </div>

          {(() => {
            const s = product.stock_status || (product.stock <= 0 ? 'out_of_stock' : product.stock <= 3 ? 'low_stock' : 'in_stock');
            const cfg = s === 'out_of_stock'
              ? { dot: 'bg-red-500',    text: 'text-red-600',    label: 'Out of stock' }
              : s === 'low_stock'
                ? { dot: 'bg-orange-500', text: 'text-orange-600', label: typeof product.stock === 'number' ? `Only ${product.stock} left` : 'Low stock' }
                : { dot: 'bg-green-500',  text: 'text-green-700',  label: 'In stock' };
            return (
              <div className="mt-6 flex items-center gap-2" data-testid="product-stock-status">
                <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                <span className={`font-semibold ${cfg.text}`}>{cfg.label}</span>
              </div>
            );
          })()}

          <div className="mt-5">
            <div className="text-sm text-neutral-900 font-semibold mb-2">Size - {size}</div>
            <div className="grid grid-cols-2 gap-3">
              {product.sizes.map(s => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`py-3 px-3 rounded-md text-sm font-medium border transition-all ${size === s ? 'bg-[#b8935a] text-white border-[#b8935a]' : 'bg-white text-[#b8935a] border-[#b8935a]/60 hover:border-[#b8935a]'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="text-neutral-800"><b>{10 + Math.floor(Math.random() * 20)}</b> people are viewing this product</span>
          </div>

          {(() => {
            const soldOut = product.stock_status === 'out_of_stock' || (typeof product.stock === 'number' && product.stock <= 0);
            return (
              <>
                <button
                  onClick={() => !soldOut && onAddToCart({ ...product, size })}
                  disabled={soldOut}
                  data-testid="add-to-cart-btn"
                  className={`mt-5 w-full font-bold text-lg py-4 rounded-lg tracking-wide transition-colors flex items-center justify-center gap-2 ${
                    soldOut ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed' : 'bg-[#1e3a2b] hover:bg-[#2a5040] text-white'
                  }`}
                >
                  <ShoppingCart className="w-5 h-5" /> {soldOut ? 'OUT OF STOCK' : 'ADD TO CART'}
                </button>
                <button
                  onClick={() => !soldOut && onAddToCart({ ...product, size })}
                  disabled={soldOut}
                  data-testid="buy-it-now-btn"
                  className={`mt-3 w-full font-bold text-lg py-4 rounded-lg tracking-wide transition-colors flex items-center justify-center gap-2 ${
                    soldOut ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed' : 'bg-[#0f2419] hover:bg-[#1e3a2b] text-white'
                  }`}
                >
                  <Zap className="w-5 h-5" /> {soldOut ? 'NOTIFY ME WHEN AVAILABLE' : 'BUY IT NOW'}
                </button>
              </>
            );
          })()}

          {/* You may also like */}
          <div className="mt-8 border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-neutral-900">You may also like</h3>
              <div className="flex items-center gap-1">
                <button className="p-1.5 rounded-full hover:bg-neutral-100"><ChevronLeft className="w-4 h-4" /></button>
                <button className="p-1.5 rounded-full hover:bg-neutral-100"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="space-y-3">
              {suggestions.slice(0, 2).map(s => (
                <div key={s.id} className="flex items-center gap-3">
                  <Link to={`/product/${s.slug}`} className="shrink-0">
                    <img src={s.image} alt="" className="w-14 h-14 rounded-md object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${s.slug}`} className="font-medium text-sm text-neutral-900 line-clamp-1 hover:text-[#b8935a]">{s.title.split('|')[0]}</Link>
                    <div className="text-xs text-neutral-600">{s.sizes[0]}</div>
                    <div className="text-sm mt-0.5">
                      <span className="text-red-600 font-semibold">Rs. {s.price.toLocaleString('en-IN')}</span>{' '}
                      <span className="text-neutral-500 line-through text-xs">Rs. {s.originalPrice.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onAddToCart({ ...s, size: s.sizes[0] })}
                    className="shrink-0 bg-[#0f2419] hover:bg-[#1e3a2b] text-white text-sm font-medium px-3 py-2 rounded-md flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 border-2 border-[#1e3a2b] rounded-lg py-3 px-4 text-center text-[#1e3a2b] font-semibold text-sm flex items-center justify-center gap-2 flex-wrap">
            1 ORDER = 1 MEALS FOR FURRY FRIENDS. <PawPrint className="w-4 h-4" /> <a href="#" className="underline">Learn more..</a>
          </div>

          {/* Countdown */}
          <div className="mt-6 bg-[#1e3a2b] rounded-xl p-5 text-white text-center">
            <div className="text-xl font-bold mb-3">Hurry! Sale ends in:</div>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              {[{ v: pad(time.h), l: 'Hrs' }, { v: pad(time.m), l: 'Mins' }, { v: pad(time.s), l: 'Secs' }].map((box, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-2xl font-bold">:</span>}
                  <div>
                    <div className="flex gap-1">
                      {box.v.split('').map((d, k) => (
                        <div key={k} className="bg-black w-10 sm:w-12 h-12 sm:h-14 rounded-md grid place-items-center text-2xl sm:text-3xl font-bold">{d}</div>
                      ))}
                    </div>
                    <div className="text-xs mt-1">{box.l}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Timeline */}
          {/* <div className="mt-8 relative">
            <div className="absolute top-6 left-[16%] right-[16%] h-0.5 bg-[#b8935a]" />
            <div className="grid grid-cols-3 relative">
              {[
                { Icon: ShoppingCart, date: 'Aug 6th', label: 'Ordered' },
                { Icon: Truck, date: 'Aug 7th - Aug 8th', label: 'Order Ready' },
                { Icon: Gift, date: 'Aug 16th - Aug 18th', label: 'Delivered' },
              ].map((s, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className="bg-[#b8935a] text-white w-12 h-12 rounded-full grid place-items-center relative z-10">
                    <s.Icon className="w-5 h-5" />
                  </div>
                  <div className="mt-2 font-bold text-sm text-neutral-900">{s.date}</div>
                  <div className="text-xs text-neutral-600">{s.label}</div>
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 lg:mt-16">
        <div className="grid grid-cols-3 border-b border-neutral-300">
          {tabs.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              className={`px-2 py-3 text-xs sm:text-sm font-semibold text-center transition-colors ${tab === i ? 'border-b-2 border-[#1e3a2b] text-[#1e3a2b]' : 'text-neutral-500 hover:text-[#1e3a2b]'}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="pt-6 pb-4 max-w-4xl">
          {tab === 0 && (
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl text-neutral-900 mb-4">{product.title.split('|')[0]}</h3>
             <div className="text-neutral-700 leading-relaxed">
  <div
    dangerouslySetInnerHTML={{
      __html: product.short_description || ''
    }}
  />

  <div
    dangerouslySetInnerHTML={{
      __html: product.description || ''
    }}
  />
</div>
              {/* <p className="mt-3 text-neutral-700 leading-relaxed">
                Each piece is <b>crafted by hand</b>, ensuring unique resin patterns that make every piece different from the last. Its <b>glossy finish and artistic detail</b> make it the perfect addition to both modern and luxury interiors.
              </p>
              {expanded && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 text-neutral-900 font-semibold"><Zap className="w-4 h-4 text-[#b8935a]" /> Key Features:</div>
                  <ul className="mt-2 list-disc pl-6 space-y-1 text-neutral-700">
                    <li>Handmade resin artistry — each piece is one-of-a-kind</li>
                    <li>Premium 22mm thick design with laminated back</li>
                    <li>Sustainably sourced live-edge wood</li>
                    <li>Delivered fully assembled, ready to use</li>
                    <li>Free doorstep delivery on all prepaid orders</li>
                  </ul>
                </div>
              )}
              <button onClick={() => setExpanded(!expanded)} className="mt-3 text-neutral-900 underline text-sm font-medium">
                {expanded ? 'Read less' : 'Read more'}
              </button> */}
            </div>
          )}
          {tab === 1 && (
            <ul className="list-disc pl-5 space-y-2 text-neutral-700">
              <li>Fresh, handpicked flowers arranged by skilled florists.</li>
                  <li>Premium-quality blooms sourced from trusted growers.</li>
                  <li>Beautifully arranged and delivered fresh to your doorstep.</li>
                  <li>Same-day delivery available on selected flower orders.</li>
            </ul>
          )}
          {tab === 2 && (
            <div className="text-neutral-700 space-y-2">
              <p>As flowers are perishable and prepared fresh for each order, we generally do not accept returns or cancellations for change of mind.</p>
<p>In case of damaged, incorrect, or poor-quality flowers, please contact us within 24 hours of delivery with clear photos. Our team will review the issue and arrange a suitable replacement or resolution.</p>
            </div>
          )}
        </div>
      </div>

      {/* Reviews section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
        <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Reviews</h2>
        <ProductReviews product={product} />
      </div>

      {/* Related products */}
      {suggestions.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <h2 className="font-serif text-3xl sm:text-4xl text-neutral-900">
              <em>You may also</em> <span className="text-[#7a1e1e] italic">like</span>
            </h2>
            <Link to={`/collection/${product.category}`} className="text-sm text-[#1e3a2b] hover:text-[#b8935a] font-semibold">View all &rarr;</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {suggestions.slice(0, 4).map(p => (
              <ProductCard key={p.id} product={p} onOpen={() => onAddToCart({ ...p, size: p.sizes[0] })} />
            ))}
          </div>
        </section>
      )}

      {/* Sticky bottom add-to-cart */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-neutral-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-4">
          <img src={product.image} alt="" className="w-11 h-11 sm:w-14 sm:h-14 rounded-md object-cover shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm sm:text-base font-semibold text-neutral-900 line-clamp-1">{product.title.split('|')[0]}</div>
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <span className="font-bold text-neutral-900">Rs. {product.price.toLocaleString('en-IN')}.00</span>
              <span className="text-neutral-500 line-through hidden sm:inline">Rs. {product.originalPrice.toLocaleString('en-IN')}.00</span>
              <span className="hidden md:inline-flex items-center gap-1 bg-[#b8935a]/20 text-[#8b6c3d] px-2 py-0.5 rounded text-xs font-semibold">
                <Tag className="w-3 h-3" /> SAVE Rs. {product.save.toLocaleString('en-IN')}
              </span>
            </div>
          </div>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            className="hidden sm:block border border-neutral-300 rounded-md px-3 py-2 text-sm text-neutral-800 bg-white focus:outline-none focus:border-[#b8935a] max-w-[180px] truncate"
          >
            {product.sizes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(() => {
            const soldOut = product.stock_status === 'out_of_stock' || (typeof product.stock === 'number' && product.stock <= 0);
            return (
              <button
                onClick={() => !soldOut && onAddToCart({ ...product, size })}
                disabled={soldOut}
                data-testid="sticky-add-to-cart-btn"
                className={`font-semibold text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-md whitespace-nowrap transition-colors flex items-center gap-2 ${
                  soldOut ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed' : 'bg-[#1e3a2b] hover:bg-[#2a5040] text-white'
                }`}
              >
                <span className="hidden sm:inline">{soldOut ? '' : `Rs. ${product.price.toLocaleString('en-IN')}.00 • `}</span>{soldOut ? 'Sold out' : 'Add to cart'}
              </button>
            );
          })()}
        </div>
      </div>
    </main>
  );
}